import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Download, MapPin, Clock, CreditCard, Heart, Sparkles, Leaf, Droplets, MessageCircle, Users, ShieldCheck } from 'lucide-react';

const BOOKING_URL = 'https://book.abbamassage.com/?aff=29';
const WHATSAPP_URL = 'https://wa.me/5219983032313?text=' + encodeURIComponent('Hello ABBA Massage, I would like to book an in-home massage.');

const PRICE_LIST = {
  en: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/uy0hf9bf_prices-abba-2026-en.png',
  es: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/icmd5yzn_precios-abba-2026-es.png'
};

const IMAGES = {
  hero: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/36p207b8_abba-masaje-a-domicilio-horizontal.png',
  terrace: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/60m6ckf1_abba-in-home-setup-terrace.png',
  villa: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/22y8j4rx_abba-villa-blanca-setup.jpg',
  prenatal: 'https://customer-assets-eiarnc6j.emergentagent.net/job_b8ee28e8-faf1-498f-b060-6a910d476ac1/artifacts/kvj4e0y5_abba-prenatal-villa.jpg',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const FAQS = [
  {
  "es": "¿Pueden realizar el masaje en mi hotel?",
  "en": "Can you provide a massage at my hotel?",
  "aes": "Sí, podemos atenderte en hoteles que no cuentan con spa, siempre que el hotel autorice la entrada de nuestros terapeutas. Tú debes gestionar el permiso directamente con el hotel y confirmarnos el acceso antes de la cita. Si el hotel tiene spa o no autoriza la entrada, puedes reservar tu masaje en nuestro estudio en Cancún.",
  "aen": "Yes, we can visit hotels without a spa, provided the hotel approves access for our therapists. You must arrange permission directly with your hotel and confirm access with us before your appointment. If your hotel has a spa or does not allow access, you can book your massage at our Cancun studio."
},
  {
    es: '¿En qué zonas ofrecen masaje a domicilio?',
    en: 'What areas do you serve for in-home massage?',
    aes: 'Ofrecemos masaje a domicilio en Cancún, Zona Hotelera, Puerto Morelos, Playa del Carmen, Isla Mujeres e Isla Blanca.',
    aen: 'We offer in-home massage in Cancun, the Hotel Zone, Puerto Morelos, Playa del Carmen, Isla Mujeres and Isla Blanca.'
  },
  {
    es: '¿Hay un mínimo de personas o servicios?',
    en: 'Is there a minimum number of guests or services?',
    aes: 'No. Una sola sesión de 60 minutos es suficiente para solicitar el servicio a domicilio. Confirma por WhatsApp la disponibilidad y logística para tu ubicación.',
    aen: 'No. One 60-minute session is enough to request in-home service. Confirm availability and travel logistics for your location through WhatsApp.'
  },
  {
    es: '¿Qué llevan para realizar el masaje?',
    en: 'What do you bring for an in-home massage?',
    aes: 'Llevamos mesa profesional, sábanas, aceites y lo necesario para la sesión. Solo necesitamos aproximadamente 2 x 2 metros de espacio y un ambiente tranquilo.',
    aen: 'We bring a professional massage table, linens, oils and what is needed for the session. We only need approximately 2 x 2 meters of space and a quiet environment.'
  },
  {
    es: '¿Puedo elegir el tipo de masaje?',
    en: 'Can I choose the type of massage?',
    aes: 'Sí. Cada sesión se personaliza. El masaje Thai y las piedras calientes se ofrecen en sesiones de 90 minutos. El masaje prenatal se ofrece en sesiones de 60 minutos.',
    aen: 'Yes. Every session is personalized. Thai massage and hot stones are available in 90-minute sessions. Prenatal massage is available in 60-minute sessions.'
  },
  {
    es: '¿Ofrecen masaje para parejas, grupos o retiros?',
    en: 'Do you offer massages for couples, groups or retreats?',
    aes: 'Sí. Coordinamos terapeutas para parejas, familias, grupos y retiros en villas, Airbnb, rentas vacacionales y residencias privadas, sujeto a disponibilidad.',
    aen: 'Yes. We coordinate therapists for couples, families, groups and retreats at villas, Airbnbs, vacation rentals and private residences, subject to availability.'
  },
  {
    es: '¿Con cuánto tiempo de anticipación debo reservar?',
    en: 'How far in advance should I book?',
    aes: 'Recomendamos reservar con al menos 24 horas de anticipación. Las citas para el mismo día pueden estar disponibles dependiendo de nuestra agenda.',
    aen: 'We recommend booking at least 24 hours in advance. Same-day appointments may be available depending on our schedule.'
  }
];

export const InHomeMassage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    const isSpanish = language === 'es';
    const canonicalUrl = 'https://abbamassage.com/in-home-massage';
    const title = isSpanish
      ? 'Masaje a domicilio en Cancún y alrededores | ABBA Massage'
      : 'In-Home Massage in Cancun & Nearby Areas | ABBA Massage';
    const description = isSpanish
      ? 'Reserva masaje profesional a domicilio en Cancún, Isla Mujeres, Isla Blanca, Puerto Morelos y Playa del Carmen. Servicio en Airbnb, villas y residencias.'
      : 'Book a professional in-home massage in Cancun, Isla Mujeres, Isla Blanca, Puerto Morelos, and Playa del Carmen. Service for Airbnbs, villas, and residences.';

    document.title = title;

    const setMeta = (attribute, key, value) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', IMAGES.hero);
    setMeta('property', 'og:locale', isSpanish ? 'es_MX' : 'en_US');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', IMAGES.hero);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

   const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      '@id': 'https://abbamassage.com/in-home-massage#service',
      name: isSpanish ? 'Masaje a domicilio en Cancún' : 'In-Home Massage in Cancun',
      description,
      serviceType: isSpanish ? 'Masaje profesional a domicilio' : 'Professional in-home massage',
      url: canonicalUrl,
      provider: {
        '@id': 'https://abbamassage.com/#business'
      },
      areaServed: [
        { '@type': 'City', name: 'Cancún' },
        { '@type': 'City', name: 'Isla Mujeres' },
        { '@type': 'Place', name: 'Isla Blanca' },
        { '@type': 'City', name: 'Puerto Morelos' },
        { '@type': 'City', name: 'Playa del Carmen' }
      ],
      audience: {
        '@type': 'Audience',
        audienceType: 'Travelers, Airbnb guests, villa guests, residents and families'
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: isSpanish
          ? 'Masajes a domicilio de ABBA Massage'
          : 'ABBA Massage In-Home Services',
        itemListElement: [
          {
            '@type': 'Offer',
            name: isSpanish
              ? 'Masaje a domicilio de 60 minutos'
              : '60-Minute In-Home Massage',
            price: '1600',
            priceCurrency: 'MXN',
            availability: 'https://schema.org/InStock',
            url: BOOKING_URL
          },
          {
            '@type': 'Offer',
            name: isSpanish
              ? 'Masaje a domicilio de 90 minutos'
              : '90-Minute In-Home Massage',
            price: '2200',
            priceCurrency: 'MXN',
            availability: 'https://schema.org/InStock',
            url: BOOKING_URL
          }
        ]
      }
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://abbamassage.com/in-home-massage#breadcrumb',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: isSpanish ? 'Inicio' : 'Home', item: 'https://abbamassage.com/' },
        { '@type': 'ListItem', position: 2, name: isSpanish ? 'Masaje a domicilio' : 'In-Home Massage', item: canonicalUrl }
      ]
    },
    {
      '@type': 'FAQPage',
      '@id': 'https://abbamassage.com/in-home-massage#faq',
      mainEntity: FAQS.map((faq) => ({
        '@type': 'Question',
        name: isSpanish ? faq.es : faq.en,
        acceptedAnswer: { '@type': 'Answer', text: isSpanish ? faq.aes : faq.aen }
      }))
    }
  ]
};


    const staticSchema = document.getElementById('in-home-massage-static-schema');
    let schemaScript = null;

    if (!staticSchema) {
      schemaScript = document.head.querySelector('#in-home-massage-schema');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'in-home-massage-schema';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }

    return () => {
      schemaScript?.remove();
    };
  }, [language]);


  const massageServices = [
    { key: 'relaxation', icon: Heart },
    { key: 'deepTissue', icon: Sparkles },
    { key: 'lymphatic', icon: Droplets },
    { key: 'muscleRelease', icon: Sparkles },
    { key: 'thai', icon: Heart },
    { key: 'facialSculpting', icon: Leaf },
  ];

  const additionalServices = [
    { key: 'organicFacial', icon: Sparkles },
    { key: 'bodyExfoliation', icon: Leaf },
  ];

  const benefits = [
    { icon: MapPin, text: language === 'es' ? 'Vamos a tu ubicación' : 'We come to your location' },
    { icon: Clock, text: language === 'es' ? 'Horarios flexibles' : 'Flexible scheduling' },
    { icon: CreditCard, text: language === 'es' ? 'Múltiples métodos de pago' : 'Multiple payment options' },
  ];

  // Service area text
  const serviceAreaText = language === 'es'
    ? 'Ofrecemos masajes a domicilio en Cancún, Isla Mujeres, Isla Blanca, Puerto Morelos y Playa del Carmen.'
    : 'We provide in-home massage in Cancún, Isla Mujeres, Isla Blanca, Puerto Morelos, and Playa del Carmen.';

  return (
    <div data-testid="in-home-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt={language === 'es' ? 'Masaje profesional a domicilio en Cancún de ABBA Massage' : 'ABBA Massage professional in-home massage service in Cancun'}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
              {t('inHome.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              {language === 'es'
                ? 'Masaje a domicilio en tu casa, villa o Airbnb'
                : 'Massage at Your Home, Villa or Airbnb'}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-base md:text-lg text-white/90 mb-4 font-['Manrope']" data-testid="in-home-hero-areas">
              Cancún · Puerto Morelos · Playa del Carmen · Isla Mujeres · Isla Blanca
            </motion.p>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/80 mb-8 font-['Manrope']">
              {t('inHome.description')}
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                  {t('cta.bookNow')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a 
                href={PRICE_LIST[language]} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-[#1C1C1C] rounded-full px-8 py-6 font-['Manrope'] font-semibold"
                  data-testid="download-prices-btn"
                >
                  <Download className="mr-2 w-5 h-5" />
                  {language === 'es' ? 'Ver Lista de Precios' : 'View Price List'}
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <nav aria-label={language === 'es' ? 'Migas de pan' : 'Breadcrumb'} className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 text-sm font-['Manrope'] text-[#5A5A5A]">
          <Link to="/" className="hover:text-[#A91B60]">{language === 'es' ? 'Inicio' : 'Home'}</Link>
          <span className="mx-2" aria-hidden="true">/</span>
          <span aria-current="page">{language === 'es' ? 'Masaje a domicilio' : 'In-Home Massage'}</span>
        </div>
      </nav>

      {/* Benefits Bar */}
      <section className="bg-[#A91B60] py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 text-white">
                <benefit.icon className="w-5 h-5 text-white/70" />
                <span className="text-sm font-['Manrope']">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Direct answer and visible pricing */}
      <section className="py-16 md:py-20 px-6 bg-white" data-testid="in-home-answer">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.25fr_1fr] gap-10 items-start">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">
              {language === 'es' ? 'Spa profesional en tu ubicación' : 'Professional spa service at your location'}
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-5">
              {language === 'es' ? 'Nosotros llevamos la experiencia completa' : 'We bring the complete experience'}
            </h2>
            <p className="text-lg text-[#5A5A5A] leading-relaxed font-['Manrope'] mb-5">
              {language === 'es'
                ? 'Llevamos mesa profesional, sábanas, aceites y lo necesario para crear una sesión personalizada en tu casa, villa, Airbnb o renta vacacional. Una sola sesión de 60 minutos es suficiente para solicitar el servicio.'
                : 'We bring a professional massage table, linens, oils and what is needed to create a personalized session at your home, villa, Airbnb or vacation rental. One 60-minute session is enough to request the service.'}
            </p>
            <p className="text-[#5A5A5A] leading-relaxed font-['Manrope'] mb-5" data-testid="hotel-access-note">
              {language === 'es'
                ? '¿Te hospedas en un hotel sin spa? Con gusto podemos atenderte ahí. Es necesario que gestiones directamente con el hotel la autorización de entrada para nuestros terapeutas y nos confirmes el acceso antes de la cita.'
                : 'Staying at a hotel without a spa? We would be happy to come to you. You must arrange entry approval for our therapists directly with your hotel and confirm access with us before your appointment.'}
            </p>
            <p className="text-[#5A5A5A] leading-relaxed font-['Manrope']">
              {language === 'es'
                ? 'Recomendamos reservar con 24 horas de anticipación. La disponibilidad para el mismo día depende de la agenda y la ubicación.'
                : 'We recommend booking 24 hours in advance. Same-day availability depends on our schedule and your location.'}
            </p>
          </div>
          <div className="bg-[#F8F6F3] rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1C1C] mb-6">
              {language === 'es' ? 'Precios de masaje a domicilio' : 'In-home massage prices'}
            </h2>
            <div className="space-y-4 font-['Manrope']">
              <div className="flex justify-between items-baseline border-b border-gray-200 pb-4">
                <span>{language === 'es' ? '60 minutos' : '60 minutes'}</span><strong className="text-xl text-[#A91B60]">$1,600 MXN</strong>
              </div>
              <div className="flex justify-between items-baseline border-b border-gray-200 pb-4">
                <span>{language === 'es' ? '90 minutos' : '90 minutes'}</span><strong className="text-xl text-[#A91B60]">$2,200 MXN</strong>
              </div>
              <p className="text-sm text-[#5A5A5A]">
                {language === 'es' ? 'Sin mínimo de personas: puedes reservar una sola sesión.' : 'No guest minimum: you may book a single session.'}
              </p>
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="block mt-7">
              <Button className="w-full bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full py-6 font-['Manrope'] font-semibold">
                <MessageCircle className="mr-2 w-5 h-5" />
                {language === 'es' ? 'Confirmar por WhatsApp' : 'Confirm on WhatsApp'}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-12 bg-[#F8F6F3]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <MapPin className="w-6 h-6 text-[#A91B60]" />
              <h3 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1C1C1C]">
                {language === 'es' ? 'Áreas de Servicio' : 'Service Areas'}
              </h3>
            </div>
            <p className="text-lg text-[#5A5A5A] font-['Manrope']">
              {serviceAreaText}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Couples, groups and trust */}
      <section className="py-20 md:py-24 px-6 bg-white" data-testid="in-home-groups">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-5">
              {language === 'es' ? 'Una sesión individual o bienestar para todo tu grupo' : 'One individual session or wellness for your entire group'}
            </h2>
            <p className="text-lg text-[#5A5A5A] font-['Manrope'] leading-relaxed">
              {language === 'es'
                ? 'Coordinamos experiencias para viajeros individuales, parejas, familias, grupos y retiros. Para varias sesiones asignamos terapeutas y horarios de acuerdo con la ubicación y disponibilidad.'
                : 'We coordinate experiences for solo travelers, couples, families, groups and retreats. For multiple sessions, we assign therapists and times according to location and availability.'}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Users, es: 'Parejas, grupos y retiros', en: 'Couples, groups & retreats', aes: 'Coordinación personalizada por WhatsApp.', aen: 'Personalized coordination through WhatsApp.' },
              { icon: ShieldCheck, es: 'Equipo profesional y experimentado', en: 'Professional, experienced team', aes: 'Terapeutas con entre 8 y 22 años de experiencia.', aen: 'Therapists with 8 to 22 years of experience.' },
              { icon: Sparkles, es: 'Sesión diseñada con intención', en: 'A session designed with intention', aes: 'Presión y técnicas adaptadas a lo que tu cuerpo necesita.', aen: 'Pressure and techniques adapted to what your body needs.' }
            ].map((item, index) => (
              <div key={index} className="rounded-2xl bg-[#F8F6F3] p-7 text-center">
                <item.icon className="w-8 h-8 text-[#A91B60] mx-auto mb-4" />
                <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C] mb-2">{language === 'es' ? item.es : item.en}</h3>
                <p className="text-[#5A5A5A] font-['Manrope']">{language === 'es' ? item.aes : item.aen}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-8 font-['Manrope'] text-[#5A5A5A]">
            {language === 'es' ? '¿Prefieres venir al estudio?' : 'Would you rather visit the studio?'}{' '}
            <Link to="/studio-services" className="text-[#A91B60] font-semibold hover:underline">
              {language === 'es' ? 'Conoce nuestro estudio privado en Cancún.' : 'Discover our private massage studio in Cancun.'}
            </Link>
          </p>
        </div>
      </section>

      {/* Massage Services - Clean Layout like Studio Services */}
      <section className="py-24 md:py-32 px-6" data-testid="massage-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-4 font-['Manrope'] font-semibold">
              {language === 'es' ? 'Nuestros Servicios' : 'Our Services'}
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] tracking-tight mb-6">
              {language === 'es' ? 'Terapias de Masaje' : 'Massage Therapies'}
            </motion.h2>
            
            {/* Price List Button */}
            <motion.div variants={fadeInUp} className="flex flex-col items-center gap-2">
              <a href={PRICE_LIST[language]} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#A91B60] text-[#A91B60] hover:bg-[#A91B60] hover:text-white rounded-full px-6 py-4 font-['Manrope'] font-semibold">
                  <Download className="mr-2 w-5 h-5" />
                  {language === 'es' ? 'Ver Lista de Precios' : 'View Price List'}
                </Button>
              </a>
              <p className="text-sm text-[#5A5A5A] font-['Manrope']">
                {language === 'es' 
                  ? 'Descarga nuestra lista completa de servicios y precios.'
                  : 'Download our full list of services and prices.'}
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-3 gap-6"
          >
            {massageServices.map((service) => (
              <motion.div
                key={service.key}
                variants={fadeInUp}
                className="group bg-white rounded-2xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] transition-all hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-full bg-[#F8F6F3] group-hover:bg-[#A91B60] flex items-center justify-center mx-auto mb-4 transition-colors">
                  <service.icon className="w-6 h-6 text-[#A91B60] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg text-[#1C1C1C] mb-2">
                  {t(`inHome.services.${service.key}.name`)}
                </h3>
                <p className="text-sm text-[#5A5A5A] font-['Manrope']">
                  {t(`inHome.services.${service.key}.desc`)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 md:py-32 bg-[#F8F6F3] px-6" data-testid="additional-services">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] tracking-tight">
              {language === 'es' ? 'Servicios Adicionales' : 'Additional Services'}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto"
          >
            {additionalServices.map((service) => (
              <motion.div
                key={service.key}
                variants={fadeInUp}
                className="group bg-white rounded-2xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] transition-all hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-full bg-[#F8F6F3] group-hover:bg-[#A91B60] flex items-center justify-center mx-auto mb-4 transition-colors">
                  <service.icon className="w-6 h-6 text-[#A91B60] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg text-[#1C1C1C] mb-2">
                  {t(`inHome.services.${service.key}.name`)}
                </h3>
                <p className="text-sm text-[#5A5A5A] font-['Manrope']">
                  {t(`inHome.services.${service.key}.desc`)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#A91B60]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-6 tracking-tight">
              {language === 'es' 
                ? <>¿Listo para <em className="italic text-white/80">relajarte</em>?</>
                : <>Ready to <em className="italic text-white/80">relax</em>?</>}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-white/80 mb-10 font-['Manrope']">
              {language === 'es'
                ? 'Reserva tu masaje a domicilio hoy y deja que llevemos la experiencia de spa a ti.'
                : 'Book your in-home massage today and let us bring the spa experience to you.'}
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-white hover:bg-gray-100 text-[#A91B60] rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  {language === 'es' ? 'Reservar por WhatsApp' : 'Book through WhatsApp'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
            </section>
               {/* FAQ Section */}
<div className="max-w-4xl mx-auto px-6 py-16">
  <div className="text-center mb-10">
    <p className="text-sm uppercase tracking-widest mb-3">
      {language === 'es' ? 'Preguntas frecuentes' : 'Frequently Asked Questions'}
    </p>
    <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl">
      {language === 'es'
        ? 'Antes de reservar tu masaje a domicilio'
        : 'Before booking your in-home massage'}
    </h2>
  </div>

  <div className="space-y-6">
    {FAQS.map((faq, index) => (
      <div key={index} className="border-b border-gray-200 pb-6">
        <h3 className="font-semibold text-lg mb-2">
          {language === 'es' ? faq.es : faq.en}
        </h3>
        <p className="text-gray-600 leading-relaxed">
          {language === 'es' ? faq.aes : faq.aen}
        </p>
      </div>
    ))}
  </div>
</div>
     
    </div>
  );
};

export default InHomeMassage;
